import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ListingCard from "@/components/ListingCard";
import ReferralsPanel from "@/components/ReferralsPanel";
import AccountPanel from "@/components/AccountPanel";
import AdminPanel from "@/components/AdminPanel";

function statusColor(s) {
  if (s === "approved") return "bg-emerald-100 text-emerald-800";
  if (s === "declined") return "bg-red-100 text-red-800";
  if (s === "paid") return "bg-primary text-primary-foreground";
  return "bg-amber-100 text-amber-800";
}

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [asRenter, setAsRenter] = useState([]);
  const [asOwner, setAsOwner] = useState([]);
  const [myDocks, setMyDocks] = useState([]);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/bookings/mine");
      setAsRenter(data.as_renter); setAsOwner(data.as_owner);
      const d = await api.get("/my/docks");
      setMyDocks(d.data.docks);
    } catch (e) {
      console.warn("[Dashboard] load failed:", e?.response?.status || e?.message);
    }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const updateStatus = async (booking_id, status) => {
    try {
      await api.post("/bookings/status", { booking_id, status });
      toast.success(`Booking ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const payNow = async (booking_id) => {
    try {
      const { data } = await api.post("/checkout/session", { booking_id, origin_url: window.location.origin });
      if (data.fully_covered_by_credits) {
        toast.success(`Booking paid entirely with $${data.credits_applied.toFixed(2)} in credits!`);
        await refresh();
        load();
        return;
      }
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user) return null;

  return (
    <div data-testid="dashboard-page" className="container-editorial py-10">
      <div className="text-xs uppercase tracking-[0.3em] text-accent mb-3">Your dashboard</div>
      <h1 className="font-display text-4xl mb-8">Hello, {user.name.split(" ")[0]}</h1>

      {user.credit_balance > 0 && (
        <div data-testid="header-credit-strip" className="mb-6 rounded-xl border border-accent/40 bg-accent/5 px-5 py-3 text-sm flex items-center justify-between">
          <span>You have <strong className="text-accent">${user.credit_balance.toFixed(2)}</strong> in SlipShare credits — auto-applied at your next booking.</span>
        </div>
      )}

      <Tabs defaultValue="bookings">
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="bookings" data-testid="tab-bookings">My Bookings</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Requests on my docks</TabsTrigger>
          <TabsTrigger value="mydocks" data-testid="tab-mydocks">My Listings</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account">Account</TabsTrigger>
          {user.role === "admin" && <TabsTrigger value="admin" data-testid="tab-admin">Admin</TabsTrigger>}
        </TabsList>

        <TabsContent value="bookings" className="pt-6">
          {asRenter.length === 0 ? (
            <p className="text-muted-foreground">No bookings yet. <a href="/search" className="text-primary link-underline">Find a slip</a>.</p>
          ) : (
            <div className="space-y-3">
              {asRenter.map((b) => (
                <div key={b.booking_id} data-testid={`renter-booking-${b.booking_id}`} className="rounded-xl border border-border bg-card p-5 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="font-display text-lg">{b.dock_title}</div>
                    <div className="text-sm text-muted-foreground">
                      {b.dock_lake} · {b.duration_quantity ?? b.months ?? 0} {b.duration_unit ? (b.duration_quantity === 1 ? b.duration_unit.replace("ly","") : b.duration_unit.replace("ly","") + "s") : "months"} from {b.start_date}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={statusColor(b.status)}>{b.status}</Badge>
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground">Total</div>
                      <div className="font-medium">${b.total_amount.toFixed(2)}</div>
                    </div>
                    {b.status === "approved" && b.payment_status !== "paid" && (
                      <Button data-testid={`pay-btn-${b.booking_id}`} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" onClick={() => payNow(b.booking_id)}>Pay now</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="pt-6">
          {asOwner.length === 0 ? (
            <p className="text-muted-foreground">No booking requests yet.</p>
          ) : (
            <div className="space-y-3">
              {asOwner.map((b) => (
                <div key={b.booking_id} data-testid={`owner-booking-${b.booking_id}`} className="rounded-xl border border-border bg-card p-5">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="font-display text-lg">{b.dock_title}</div>
                      <div className="text-sm text-muted-foreground">
                        From {b.renter_name} · {b.duration_quantity ?? b.months ?? 0} {b.duration_unit ? (b.duration_quantity === 1 ? b.duration_unit.replace("ly","") : b.duration_unit.replace("ly","") + "s") : "months"} from {b.start_date}
                      </div>
                      {b.message && <div className="mt-2 text-sm italic text-foreground/80">"{b.message}"</div>}
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={statusColor(b.status)}>{b.status}</Badge>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">Total</div>
                        <div className="font-medium">${b.total_amount.toFixed(2)}</div>
                      </div>
                      {b.status === "pending" && (
                        <>
                          <Button data-testid={`decline-btn-${b.booking_id}`} variant="outline" onClick={() => updateStatus(b.booking_id, "declined")}>Decline</Button>
                          <Button data-testid={`approve-btn-${b.booking_id}`} className="bg-primary hover:bg-primary/90" onClick={() => updateStatus(b.booking_id, "approved")}>Approve</Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mydocks" className="pt-6">
          <div className="mb-6">
            <Button data-testid="new-dock-btn" className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full" onClick={() => navigate("/list")}>+ New listing</Button>
          </div>
          {myDocks.length === 0 ? (
            <p className="text-muted-foreground">You haven't listed a dock yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {myDocks.map((d) => <ListingCard key={d.dock_id} dock={d}/>)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="referrals" className="pt-6">
          <ReferralsPanel />
        </TabsContent>

        <TabsContent value="account" className="pt-6">
          <AccountPanel />
        </TabsContent>

        {user.role === "admin" && (
          <TabsContent value="admin" className="pt-6">
            <AdminPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
